/**
 * A role's pay, in the unit the role states.
 *
 * **Two scales, and nothing converts between them.** A yearly figure is in
 * thousands, so `170` means $170,000. An hourly figure is whole currency, so
 * `45` means $45 an hour.
 *
 * A second copy of `backend/src/lib/pay.ts`, because the frontend does not
 * import backend code. Change both.
 *
 * Nothing multiplies an hourly rate out to a year. A document saying "$45 per
 * hour" used to have nowhere to go but a box that only understood thousands,
 * so it was annualised, and a figure the customer never stated ended up in
 * their own record.
 */
export type PayUnit = 'year' | 'hour'

/** Absent reads as a year, which is what every role saved before this means. */
export const payUnitOf = (unit: PayUnit | undefined): PayUnit =>
  unit === 'hour' ? 'hour' : 'year'

export const money = (k: number, unit: PayUnit = 'year') =>
  unit === 'hour'
    ? '$' + Number(k).toLocaleString('en-US', { maximumFractionDigits: 2 })
    : '$' + Math.round(k * 1000).toLocaleString('en-US')

export const moneyK = (k: number, unit: PayUnit = 'year') =>
  unit === 'hour'
    ? '$' + Number(k).toLocaleString('en-US', { maximumFractionDigits: 2 })
    : '$' + Math.round(k) + 'k'

/** What a period is called at the end of a sentence. */
export const perLabel = (unit: PayUnit) => (unit === 'hour' ? 'an hour' : 'a year')

/** The short form, for a table cell where the column already says the period. */
export const perShort = (unit: PayUnit) => (unit === 'hour' ? '/hr' : '/yr')

/**
 * Whether a role says what it pays.
 *
 * **Zero is how "not stated" is stored.** A document that names no salary
 * comes out of extraction as an empty string, and `parseFloat('') || 0` writes
 * it down as 0. That is safe only because no real role pays nothing, and it is
 * safe only while nothing renders it as a figure.
 *
 * Four roles in the demo library are medical writing job descriptions that
 * name no salary, which is normal for that kind of document. Every screen
 * showing them said the range was $0 to $0, and so did the job description a
 * customer downloads and hands to a candidate.
 *
 * One function decides this, and the formatters below are the only things that
 * turn pay into words.
 */
export const statesPay = (min: number, max: number): boolean =>
  (Number(min) > 0) || (Number(max) > 0)

/**
 * A range in full, or plain words when the role names no pay.
 *
 * **The unit is always said.** A bare range is a range somebody has to guess
 * the period of, and the guess that costs the most reads $45 as a salary.
 */
export const rangeLong = (min: number, max: number, unit?: PayUnit) => {
  const u = payUnitOf(unit)
  return statesPay(min, max)
    ? `${money(min, u)} to ${money(max, u)} ${perLabel(u)}`
    : 'Not set'
}

/**
 * The same, short enough for a table cell.
 *
 * **Only an hourly range carries its period here.** The yearly form is already
 * unambiguous: `$150k` is thousands a year and nothing else, and no hourly
 * rate is ever written with a k. Putting `/yr` on every row would be noise on
 * the rule to mark the exception, and a column of it reads as a column of
 * nothing.
 *
 * The long form says the period either way, because that one is read on its
 * own rather than against a column of its neighbours.
 */
export const rangeShort = (min: number, max: number, unit?: PayUnit) => {
  const u = payUnitOf(unit)
  if (!statesPay(min, max)) return 'Not set'
  return u === 'hour'
    ? `${moneyK(min, u)} to ${moneyK(max, u)}${perShort(u)}`
    : `${moneyK(min, u)} to ${moneyK(max, u)}`
}

export const today = () => new Date().toISOString().slice(0, 10)

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

/**
 * A count, with its thousands grouped.
 *
 * A library really does hold thousands now, and "5000 job descriptions" reads
 * like a raw value rather than a count. It also sat next to a progress line
 * that said "Imported 2,225 of 5,000", so the same screen wrote the same kind
 * of number two ways.
 */
export const count = (n: number) => n.toLocaleString()

export const plural = (n: number, one: string, many = one + 's') =>
  `${count(n)} ${n === 1 ? one : many}`

export const splitLines = (v: string) =>
  v.split('\n').map((x) => x.trim()).filter(Boolean)

export const splitCommas = (v: string) =>
  v.split(',').map((x) => x.trim()).filter(Boolean)
